import React, { useEffect, useReducer } from 'react'
import clsx from 'clsx'
import effects from 'app/effects/effects'
import {
  CHANNEL_SMS,
  CHANNEL_EMAIL,
  CHANNEL_APP,
  CHANNEL_CALL,
} from 'app/constants/channel'
import {
  Typography,
  Tooltip,
  makeStyles,
  CircularProgress,
} from '@material-ui/core'
import * as R from 'ramda'
import { useIntl } from 'react-intl'
import Assets from 'app/configs/assetConfigs'
import { useAppName, useAppLogo } from 'commons/usePartner'

const useStyles = makeStyles((theme) => ({
  textVia: {
    color: `${theme.palette.informative.main} !important`,
    textDecoration: 'underline',
    cursor: 'text',
    paddingLeft: '4px',
    paddingRight: '4px',
  },
  tooltipBox: {
    width: '200px',
    paddingLeft: '8px',
    minHeight: '50px !important',
    verticalAlign: 'middle',
  },
  iconSize: {
    width: '15px !important',
    height: '18px !important',
    paddingTop: '4px',
  },
  notifyMeIconSize: {
    width: '15px !important',
    height: '15px !important',
    paddingTop: '4px',
  },
}))

const actionTypes = {
  OPEN_TOOLTIP: 'OPEN_TOOLTIP',
  CLOSE_TOOLTIP: 'CLOSE_TOOLTIP',
  SET_SUBSCRIBER_COUNT: 'SET_SUBSCRIBER_COUNT',
  GET_ERROR: 'GET_ERROR',
}

const initialState = {
  open: false,
  isLoading: false,
  subscriberCount: [],
  error: undefined,
}

const reducer = (state, action) => {
  switch (action.type) {
    case actionTypes.OPEN_TOOLTIP:
      return {
        ...state,
        open: true,
        isLoading: true,
      }
    case actionTypes.CLOSE_TOOLTIP:
      return {
        ...state,
        open: false,
        isLoading: false,
        subscriberCount: [],
        error: undefined,
      }
    case actionTypes.SET_SUBSCRIBER_COUNT:
      return {
        ...state,
        isLoading: false,
        subscriberCount: action.payload,
        error: undefined,
      }
    case actionTypes.GET_ERROR:
      return {
        ...state,
        isLoading: false,
        subscriberCount: [],
        error: action.payload,
      }
    default:
      return state
  }
}

export default (props) => {
  const { channels, notificationId } = props
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [{ open, isLoading, subscriberCount, error }, dispatchAction] =
    useReducer(reducer, initialState)
  const appName = useAppName()
  const { logo: appLogo } = useAppLogo()

  if (channels.length === 0) {
    return null
  }

  let viaChannel = 'Omnichannel'

  const channelOption = [
    { channelId: CHANNEL_APP, channelName: `${appName} App` },
    { channelId: CHANNEL_EMAIL, channelName: 'Email' },
    { channelId: CHANNEL_SMS, channelName: 'SMS' },
    { channelId: CHANNEL_CALL, channelName: 'Automated Call' },
  ]

  if (channels.length === 1) {
    viaChannel = R.compose(
      R.prop('channelName'),
      R.head,
      R.map((item) => R.find(R.propEq('channelId', item), channelOption)),
    )(channels)
  }

  useEffect(() => {
    if (isLoading) {
      effects.apis
        .getRecipientCountForTimeline({ notificationId })
        .then((response) => {
          dispatchAction({
            type: actionTypes.SET_SUBSCRIBER_COUNT,
            payload: response,
          })
        })
        .catch(() => {
          dispatchAction({
            type: actionTypes.GET_ERROR,
            payload: formatMessage({ id: 'error.default.label' }),
          })
        })
    }
  }, [formatMessage, isLoading, notificationId])

  return (
    <Tooltip
      open={open}
      onOpen={() => {
        dispatchAction({ type: actionTypes.OPEN_TOOLTIP })
      }}
      onClose={() => {
        dispatchAction({ type: actionTypes.CLOSE_TOOLTIP })
      }}
      title={
        isLoading ? (
          <div className={clsx(classes.tooltipBox, 'text-center')}>
            <CircularProgress color="primary" />
          </div>
        ) : (
          <TooltipDescription
            classes={classes}
            subscriberCount={subscriberCount}
            error={error}
            appName={appName}
            appLogo={appLogo}
          />
        )
      }
      placement="bottom"
      arrow
    >
      <Typography className={classes.textVia}> via {viaChannel}</Typography>
    </Tooltip>
  )
}

const TooltipDescription = (props) => {
  const { classes, subscriberCount, error } = props
  if (R.isNil(error) && !R.isEmpty(subscriberCount)) {
    return (
      <div className={classes.tooltipBox}>
        <div className="flex justify-between">
          <div className="flex">
            <img
              src={props.appLogo}
              className={clsx(classes.notifyMeIconSize, 'object-contain')}
              alt="App"
            />
            <Typography className="mx-2">{props.appName} App</Typography>
          </div>
          <Typography className="px-4">
            ({subscriberCount.applicationCount})
          </Typography>
        </div>
        <div className="flex justify-between">
          <div className="flex">
            <img
              src={Assets.channels.emailIcon}
              className={classes.iconSize}
              alt="Email"
            />
            <Typography className="mx-2">Email</Typography>
          </div>
          <Typography className="px-4">
            ({subscriberCount.emailCount})
          </Typography>
        </div>
        <div className="flex justify-between">
          <div className="flex">
            <img
              src={Assets.channels.smsIcon}
              className={classes.iconSize}
              alt="SMS"
            />
            <Typography className="mx-2">SMS</Typography>
          </div>
          <Typography className="px-4">({subscriberCount.smsCount})</Typography>
        </div>
        <div className="flex justify-between">
          <div className="flex">
            <img
              src={Assets.channels.automateCallIcon}
              className={classes.iconSize}
              alt="Automate Call"
            />
            <Typography className="mx-2">Automated Call</Typography>
          </div>
          <Typography className="px-4">
            ({subscriberCount.automateCallCount})
          </Typography>
        </div>
      </div>
    )
  }
  return (
    <div className={clsx(classes.tooltipBox, 'text-center')}>
      <div>{error}</div>
    </div>
  )
}
